/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: EmailSettingsActions.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Action buttons component for email settings list.
────────────────────────────────────────────────────────────────*/

import { Edit, Copy, TestTube, Trash2 } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import type { EmailConfig } from '../types';

interface EmailSettingsActionsProps {
  config: EmailConfig;
  onEdit: (config: EmailConfig) => void;
  onDuplicate: (config: EmailConfig) => void;
  onTest: (config: EmailConfig) => void;
  onDelete: (config: EmailConfig) => void;
}

export function EmailSettingsActions({
  config,
  onEdit,
  onDuplicate,
  onTest,
  onDelete,
}: EmailSettingsActionsProps) {
  const { t } = useI18n();

  return (
    <div className="flex items-center gap-1.5">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => onEdit(config)}
          className="p-2 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
          aria-label={t('common.edit')}
          type="button"
        >
          <Edit className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => onDuplicate(config)}
          className="p-2 text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20"
          aria-label={t('common.duplicate')}
          type="button"
        >
          <Copy className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('emailSettings.testConfiguration')} position="top">
        <button
          onClick={() => onTest(config)}
          className="p-2 text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20"
          aria-label={t('emailSettings.testConfiguration')}
          type="button"
        >
          <TestTube className="h-4 w-4" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => onDelete(config)}
          className="p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
          aria-label={t('common.delete')}
          type="button"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </SimpleTooltip>
    </div>
  );
}

